<script setup>
import TD from "@/Components/Table/Element.vue";
import SettingsLayout from "@/Layouts/Settings.vue";
import Input from "@/Components/Settings/Input.vue";
import Label from "@/Components/Settings/Label.vue";
import Button from "@/Components/Button.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import Heading from "@/Components/Settings/Heading.vue";
import Modal from "@/Components/Settings/Modal.vue";
import { Link, Head, useForm } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { onMounted, ref } from "vue";

const page_name = "Behandlungstypen";

const props = defineProps({
  types: Array,
  interval: Number,
  maxPract: Number,
  errors: Object,
});

//onMounted(() => {
//    console.log(props.types);
//})

const canEdit = Inertia.page.props.auth.can["edit settings"];
const canDelete = Inertia.page.props.auth.can["delete settings"];

const modalData = ref({
  show: false,
  id: Number,
});

function showDeleteModal(id) {
  modalData.value.id = id;
  modalData.value.show = true;
}

function deleteType(id) {
  Inertia.delete(`/types/${id}`, {}, { preserveState: true, replace: true });
  modalData.value.show = false;
}

const form = useForm({});
</script>

<template>
  <SettingsLayout>
    <Modal :show="modalData.show" @close="modalData.show = false">
      <template #title>
        <div>Warnung</div>
      </template>
      <template #message>
        <!-- {{ modalData.message }} -->
        <p>
          Die Daten des Behandlungstypen sowie zugewiesene Termine werden
          unwiderruflich gelöscht.
        </p>
      </template>
      <template #actions>
        <div class="">
          <div class="flex gap-5 justify-center mt-8">
            <Button
              class="mt-auto"
              @click="
                () => {
                  modalData.show = false;
                }
              "
              >Abbrechen</Button
            >

            <Button
              type="button"
              class="mt-auto"
              @click="deleteType(modalData.id)"
              >Löschen</Button
            >
          </div>
        </div>
      </template>
    </Modal>

    <div
      v-if="$props.errors.delete"
      class="bg-red-100 border-t-4 border-red-500 rounded-b text-red-900 px-4 py-3 shadow-md absolute top-0 right-0"
      role="alert"
    >
      <div class="flex">
        <div>
          <p class="text-sm">Es existieren Termine zu diesem Typ.</p>
        </div>
      </div>
    </div>
    <Head :title="`${page_name} - Grundeinstellungen`" />

    <template #breadcrumb>{{ page_name }}</template>
    <template #controls>
      <HeaderLink href="/" type="close">Schließen</HeaderLink>
    </template>
    <Heading title="Behandlungstypen" />

    <p class="text-sm text-oteoText mb-3">
      Bevor Sie Termine anlegen können, müssen Sie die Behandlungstypen anlegen,
      z. B. PZR, Kontrolle usw. Wählen Sie dazu bitte einen Zeitraum, einen
      Behandlungsraum und falls für die Behandlung nur ein bestimmter
      Behandler/-in in Frage kommt, einen Behandler/-in aus.
    </p>

    <p
      v-if="$page.props.auth.office_canusewidget"
      class="text-sm text-oteoText mb-3"
    >
      Wenn Sie die Online-Terminvergabe auf Ihrer Praxis-Homepage nutzen, können
      Sie geeignete Behandlungstypen für die Online-Terminvergabe freigeben.
      Diese Termine können dann von Ihren Patient/-innen über Ihre
      Praxis-Homepage gebucht werden.
    </p>

    <p
      v-if="$page.props.auth.office_canusewidget"
      class="text-sm text-oteoText"
    >
      Wie Sie die Online-Terminvergabe in Ihre Praxis-Homepage integrieren,
      erfahren Sie unter dem Punkt
      <a class="text-oteo-300" href="/einstellungen/widgeteinbindung"
        >„Online-Terminvergabe / Einbindung“</a
      >.
    </p>

    <Button
      v-if="canEdit"
      href="/einstellungen/behandlungstypen/erstellen"
      class="my-9"
    >
      Neuen Behandlungstypen anlegen
    </Button>

    <div class="flex flex-col">
      <div class="sm:-mx-6 lg:-mx-8">
        <div class="py-2 inline-block min-w-full sm:px-6 lg:px-8">
          <div class="overflow-auto max-h-[350px] max-w-[1100px]">
            <table class="min-w-full">
              <thead class="bg-white border-b sticky top-0">
                <tr>
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Benennung
                  </th>
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Kürzel
                  </th>
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Raum
                  </th>
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Behandler/in
                  </th>
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Dauer
                  </th>
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Farbe
                  </th>
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Aktion
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(type, index) in types"
                  :key="type.id"
                  class="border-b"
                  :class="{ 'bg-gray-100': index % 2 === 0 }"
                >
                  <TD
                    class="max-w-[250px] overflow-hidden text-ellipsis"
                    :title="type.name"
                    >{{ type.name }}</TD
                  >
                  <TD>{{ type.abbreviation }}</TD>
                  <TD>
                    {{ type.rooms.length > 0 ? type.rooms[0].name : "Alle" }}
                  </TD>
                  <TD>
                    {{
                      type.practitioners.length >= maxPract
                        ? "Alle"
                        : type.practitioners.length == 1
                        ? type.practitioners[0].fullname
                        : "Mehrere"
                    }}
                  </TD>
                  <TD>{{ type.duration * interval }} Min.</TD>
                  <TD>
                    <div
                      class="w-4 h-4 rounded-full m-auto"
                      :style="`background-color:${type.color}`"
                    ></div>
                  </TD>
                  <TD>
                    <div class="flex items-center text-oteo-300">
                      <Link
                        v-if="canEdit"
                        class="mr-4"
                        :href="`behandlungstypen/${type.id}/bearbeiten/`"
                      >
                        <i class="las la-angle-right"></i>bearbeiten
                      </Link>
                      <div
                        v-if="canDelete"
                        class="cursor-pointer"
                        @click="showDeleteModal(type.id)"
                      >
                        <i class="las la-times"></i>
                        löschen
                      </div>
                    </div>
                  </TD>
                </tr>

                <tr>
                  <TD></TD>
                  <TD></TD>
                  <TD></TD>
                  <TD></TD>
                  <TD></TD>
                  <TD></TD>
                  <TD></TD>
                </tr>
              </tbody>
            </table>
            <div
              class="sticky bottom-0 h-10 w-full bg-gradient-to-t from-white"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </SettingsLayout>
</template>
